/**
 * ModelMetricsPanel — the cards of the model metrics page (TRA-1461), two
 * to a row on a wide screen and one on a narrow one: requests, response
 * time, time to first token, concurrent requests, prompt tokens per second,
 * request size, prompt cache, capacity. The two-part cards, throughput and
 * errors, come last and each take a full row with their parts side by side,
 * so no one-chart card is stretched next to a taller one. Each card carries
 * its own empty state, so a model with no traffic renders every heading and
 * says under each why there is nothing to draw. Where a chart would be, the
 * empty state is a box of the chart's height with the page's arrows and
 * Reset zoom on it, so a window with no traffic can still be moved and a
 * moved page can still get back to now (TRA-1592).
 *
 * Fed from one metrics read via `contracts.ts`, cut to the page's zoom
 * (TRA-1592); a drag across any chart hands the page a new one. The
 * capacity card's content comes from the page, which knows whether the
 * model runs on our fleet.
 */

import { SectionHeading } from '@/components/models/home/ModelHomeSection'
import { ChartControls, type PagePan } from '@/components/models/home/PanControls'
import type { TimeRange } from '@/components/models/home/replicaZoom'
import type { ErrorMessageRow, MetricsView } from '@/components/models/metrics/contracts'
import { formatSeen } from '@/components/models/metrics/contracts'
import {
  ConcurrencyChart,
  type DragZoom,
  ErrorRateChart,
  PercentileChart,
  PromptCacheChart,
  PromptTokensPerSecondChart,
  RequestSizeChart,
  StatusChart,
  TokensChart,
  TokensPerSecondChart,
} from '@/components/models/metrics/MetricsCharts'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const NO_TRAFFIC = 'No requests in this window.'
const NO_TTFT =
  'No streamed requests in this window. Time to first token is measured on streamed requests only.'
const NO_GENERATION =
  'No streamed requests in this window. Tokens per second is measured on streamed requests only.'
const NO_PROMPTS = 'No prompt tokens in this window.'
const NO_LATENCY = 'Requests in this window carry no latency, so there is nothing to estimate from.'
const NO_ERRORS = 'No errors in this window.'
const NO_ERROR_MESSAGES = 'No error messages in this window.'
const ERROR_MESSAGES_UNAVAILABLE = 'Top messages could not load for this window.'
const ERROR_MESSAGES_LOADING = 'Loading...'

export function Empty({ children }: { children: string }): React.JSX.Element {
  return <p className="text-sm text-muted-foreground">{children}</p>
}

/**
 * The empty state in a chart's place: a box of the chart's height
 * (`Frame`'s plot) with the page's arrows and Reset zoom on it, as on every
 * chart, and the reason there is nothing to draw in its middle.
 */
function EmptyChart({ pan, children }: { pan: PagePan; children: string }): React.JSX.Element {
  return (
    <div className="group relative h-44 w-full">
      <ChartControls pan={pan} />
      <p className="absolute inset-0 flex items-center justify-center px-10 text-center text-sm text-muted-foreground">
        {children}
      </p>
    </div>
  )
}

/** One hairline-framed card of the grid; `wide` takes the full row. */
export function MetricsCard({
  title,
  subtitle,
  wide = false,
  children,
}: {
  title: string
  subtitle: string
  wide?: boolean
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <section className={wide ? 'border border-border p-5 md:col-span-2' : 'border border-border p-5'}>
      <SectionHeading title={title} subtitle={subtitle} />
      <div className="mt-5">{children}</div>
    </section>
  )
}

/** The two parts of a wide card side by side, stacked on a narrow screen. */
export function SideBySide({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="grid gap-8 md:grid-cols-2">{children}</div>
}

/**
 * The most frequent error messages of the window, most frequent first.
 * `unavailable` means the list could not be read (the gateway did not
 * answer it in time, or the zoomed stretch's read failed): the error-rate
 * line above it is still whole, so the card says only that the list is
 * missing. No list at all means the zoomed stretch's read is still on its
 * way (contracts.ts, errorList).
 */
export function TopErrorsTable({
  errors,
  unavailable,
}: {
  errors: ErrorMessageRow[] | null
  unavailable: boolean
}): React.JSX.Element {
  if (errors === null) return <Empty>{ERROR_MESSAGES_LOADING}</Empty>
  if (unavailable) return <Empty>{ERROR_MESSAGES_UNAVAILABLE}</Empty>
  if (errors.length === 0) return <Empty>{NO_ERROR_MESSAGES}</Empty>
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-20">Status</TableHead>
          <TableHead>Message</TableHead>
          <TableHead className="w-20 text-right">Count</TableHead>
          <TableHead className="w-36">Last seen</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {errors.map((row) => (
          <TableRow key={`${row.status_code.toString()} ${row.message}`}>
            <TableCell className="font-mono tabular-nums">{row.status_code}</TableCell>
            <TableCell className="break-words font-mono text-xs">{row.message}</TableCell>
            <TableCell className="text-right font-mono tabular-nums">
              {row.count.toLocaleString()}
            </TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground">
              {formatSeen(row.last_seen)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export function ModelMetricsPanel({
  view,
  errors,
  errorsUnavailable,
  capacity,
  onZoom,
  pan,
}: {
  view: MetricsView
  /** The stretch's top error messages; null while they are still being read. */
  errors: ErrorMessageRow[] | null
  /** True when the gateway did not answer the error list in time. */
  errorsUnavailable: boolean
  /** The capacity card's content: a replica chart, or one line saying why not. */
  capacity: React.ReactNode
  /** A drag across any chart: the page's new zoom. */
  onZoom: (zoom: TimeRange) => void
  /** The page's arrows and Reset zoom, drawn on every chart. */
  pan: PagePan
}): React.JSX.Element {
  const { buckets, hours, summary } = view
  const hasTraffic = view.requests > 0
  const drag: DragZoom = { bucketMs: view.bucketMs, onZoom, pan }
  const charts = { buckets, summary, hours, drag }
  return (
    <div className="grid gap-4 py-6 md:grid-cols-2">
      <MetricsCard title="Requests" subtitle="Requests per minute and how each bucket's requests ended">
        {hasTraffic ? <StatusChart {...charts} /> : <EmptyChart pan={pan}>{NO_TRAFFIC}</EmptyChart>}
      </MetricsCard>

      <MetricsCard
        title="Response time"
        subtitle="How long requests took from the gateway's first byte in to its last byte out"
      >
        {hasTraffic ? (
          <PercentileChart
            buckets={buckets}
            hours={hours}
            drag={drag}
            pick={(bucket) => bucket.latency}
            summary={summary.latency}
            title="Latency"
          />
        ) : (
          <EmptyChart pan={pan}>{NO_TRAFFIC}</EmptyChart>
        )}
      </MetricsCard>

      <MetricsCard title="Time to first token" subtitle="How long streamed requests waited for their first token">
        {view.hasTtft ? (
          <PercentileChart
            buckets={buckets}
            hours={hours}
            drag={drag}
            pick={(bucket) => bucket.ttft}
            summary={summary.ttft}
            title="Time to first token"
          />
        ) : (
          <EmptyChart pan={pan}>{hasTraffic ? NO_TTFT : NO_TRAFFIC}</EmptyChart>
        )}
      </MetricsCard>

      <MetricsCard title="Concurrent requests" subtitle="Estimated requests in flight at once per bucket">
        {hasTraffic && summary.inFlight !== null ? (
          <ConcurrencyChart {...charts} />
        ) : (
          <EmptyChart pan={pan}>{hasTraffic ? NO_LATENCY : NO_TRAFFIC}</EmptyChart>
        )}
      </MetricsCard>

      <MetricsCard title="Prompt tokens per second" subtitle="Prompt tokens per second of wall clock time">
        {view.hasPrompts ? (
          <PromptTokensPerSecondChart {...charts} />
        ) : (
          <EmptyChart pan={pan}>{hasTraffic ? NO_PROMPTS : NO_TRAFFIC}</EmptyChart>
        )}
      </MetricsCard>

      <MetricsCard title="Request size" subtitle="Average prompt and output tokens per request">
        {hasTraffic ? <RequestSizeChart {...charts} /> : <EmptyChart pan={pan}>{NO_TRAFFIC}</EmptyChart>}
      </MetricsCard>

      <MetricsCard title="Prompt cache" subtitle="How much of each prompt the model could reuse">
        {view.hasPrompts ? (
          <PromptCacheChart {...charts} />
        ) : (
          <EmptyChart pan={pan}>{hasTraffic ? NO_PROMPTS : NO_TRAFFIC}</EmptyChart>
        )}
      </MetricsCard>

      <MetricsCard title="Capacity" subtitle="Replicas over time">
        {capacity}
      </MetricsCard>

      <MetricsCard title="Throughput" subtitle="Output tokens per second and tokens per bucket" wide>
        {!hasTraffic ? (
          <EmptyChart pan={pan}>{NO_TRAFFIC}</EmptyChart>
        ) : view.hasGeneration ? (
          <SideBySide>
            <TokensPerSecondChart {...charts} />
            <TokensChart {...charts} />
          </SideBySide>
        ) : (
          <div className="space-y-8">
            <Empty>{NO_GENERATION}</Empty>
            <TokensChart {...charts} />
          </div>
        )}
      </MetricsCard>

      <MetricsCard title="Errors" subtitle="Error rate per bucket and the most frequent messages" wide>
        {!hasTraffic ? (
          <EmptyChart pan={pan}>{NO_TRAFFIC}</EmptyChart>
        ) : view.errors === 0 ? (
          <EmptyChart pan={pan}>{NO_ERRORS}</EmptyChart>
        ) : (
          <SideBySide>
            <ErrorRateChart {...charts} />
            <TopErrorsTable errors={errors} unavailable={errorsUnavailable} />
          </SideBySide>
        )}
      </MetricsCard>
    </div>
  )
}
